/**
 * Everything on the organization hub that touches money — which is one control, in one of two
 * shapes, and nothing else (GH-783).
 *
 * ## The two shapes, and why they are one component
 *
 * An organization the server will take money for gets a **general fund** button. An organization
 * that has not claimed its listing gets a plain statement that giving is not set up, and nothing
 * to press: it says nothing about money waiting, because there is none (GH-794 archived the
 * statement of what somebody *would* give). Both live here rather than as two blocks in
 * `OrgLanding` with a ternary between them, where the third state (approved, but the server has
 * named no fund) is easy to write as a fall-through nobody notices.
 *
 * ## The one id this component may mint against
 *
 * `generalFundEventId`, and never `liveEventId`. `directory.LIVE_EVENT` orders `is_general_fund`
 * **last**, so `liveEventId` is tonight's fundraiser whenever one is running: a button labelled for
 * the general fund that minted from it would put a fund on the donor's receipt that they did not
 * choose. Two names on the payload, two destinations, and this file reaches for exactly one of
 * them. Giving to an *event* happens on the event's own page, which the calendar links to.
 *
 * ## What the server decides and this component only renders
 *
 * Both gates are the server's answers, read from the payload: `canAcceptDonations`, and a general
 * fund existing at all (migration 0026 creates one on approval). An organization that is approved
 * but whose payload names no fund renders **nothing here** — no button, no explanation, no
 * apology. It cannot occur against a current server, and the alternative to silence would be this
 * bundle inventing a substitute id.
 *
 * It mints nothing until the button is pressed. Only the server signs a checkout context
 * (SPEC.md §5.1).
 *
 * ## The third shape: a gift held for an unclaimed organization (GH-798, DEC-046)
 *
 * When the server sends `heldGifts`, an unclaimed organization gets a Give button after all — but
 * the gift is made to the platform's fund, targeted for the organization and held until it claims
 * or the hold runs out. So the button mints from `…/held-gift-link`, never from an event id, and
 * the disclosure behind the ⓘ is the server's `notice` and `terms` verbatim. Nothing here says the
 * organization has the money; it does not (`HELD_GIFT_FORBIDDEN_PHRASES`).
 *
 * `heldGifts` null — the flag is off, or the organization is claimed or archived — leaves the
 * unclaimed shape exactly as GH-794 left it. So does a mint refused with `capability_disabled`:
 * the flag went off between the page load and the press, and the honest answer is the unclaimed
 * wording, not an error.
 *
 * ## Presets and funds on the page (GH-793 on iOS, GH-809 here)
 *
 * The owner asked for the organization's give screen to give like the event room: preset amounts,
 * and the organization's funds as chips when it publishes more than one. Those are the general
 * fund's **checkout context** — the server's presets and the server's designations — so the page
 * now reads that context as it loads, exactly as iOS does, and draws them above the button. The
 * figures are the server's; nothing here composes an amount or picks a fund.
 *
 * This changes the "mints nothing until pressed" rule in one bounded way: loading the page signs
 * one context for the general fund (a stateless signature, written nowhere) and reads it. The
 * press still mints a **fresh** one — a context expires within the hour and a page can sit open
 * longer — and carries the donor's taps to checkout as `?amount=` and `?designation=`, which
 * checkout treats as hints: the amount is still quoted by the server, and a fund the context does
 * not offer is dropped (`sendableDesignationId`). If the context cannot be read the page is the
 * single button it was, and the gift is chosen on the next screen.
 *
 * ## Arriving from a button on somebody else's page (GH-852)
 *
 * The console's Buttons & links tool makes buttons, links and QR codes that open this page with
 * the same two hints in the address — `?amount=` (from $1) and `?designation=`. They seed the taps
 * above and nothing else: the amount is preselected (as an extra chip when it is not one of the
 * presets), the fund is dropped unless this context offers it, and both still travel to checkout as
 * hints the server prices. A forged link changes a preselection on the forger's own screen.
 */
import { useEffect, useState } from "react";
import {
  MAX_SUGGESTED_AMOUNTS,
  formatMoneyCompact,
  type ContextResponse,
  type OrganizationDetailResponse,
} from "@givehere/contracts";
import { ApiError, fetchContext, fetchDonationLink, fetchHeldGiftLink } from "./api.js";
import {
  DesignationPicker,
  designationChoice,
  sendableDesignationId,
} from "./DesignationPicker.js";
import { Explain } from "./Explain.js";
import { checkoutHints, readCheckoutHints } from "./give-hints.js";

export function OrgGive({
  organization,
  heldGifts = null,
}: {
  organization: OrganizationDetailResponse["organization"];
  /** The server's disclosure when a gift may be held for this unclaimed organization. */
  heldGifts?: OrganizationDetailResponse["heldGifts"];
}) {
  const [starting, setStarting] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  /** Set when the server refused the held-gift mint as switched off: back to the unclaimed shape. */
  const [heldClosed, setHeldClosed] = useState(false);

  const generalFundEventId = organization.generalFundEventId;

  /** The general fund's own offer — presets and funds — or null until (or unless) it is read. */
  const [offer, setOffer] = useState<ContextResponse | null>(null);
  // Read once: what a button or link on another page asked to start from (GH-852).
  const [arrived] = useState(() => readCheckoutHints(window.location.search));
  const [amountMinor, setAmountMinor] = useState<string | null>(arrived.amountMinor);
  const [designationId, setDesignationId] = useState<string | null>(arrived.designationId);
  const offersGeneralFund = organization.canAcceptDonations && generalFundEventId !== null;

  useEffect(() => {
    if (!offersGeneralFund || generalFundEventId === null) return;
    let live = true;
    fetchDonationLink(generalFundEventId)
      .then((link) => fetchContext(link.token))
      .then(
        (context) => live && setOffer(context),
        // Silent: the page is then the single button it always was.
        () => undefined,
      );
    return () => {
      live = false;
    };
  }, [offersGeneralFund, generalFundEventId]);

  if (organization.canAcceptDonations) {
    if (generalFundEventId === null) return null;

    const offered = (offer?.event?.suggestedAmounts ?? []).slice(0, MAX_SUGGESTED_AMOUNTS);
    // A button's amount that is not a preset is shown as one more chip, so the donor can see it.
    const presets =
      offered.length > 0 &&
      arrived.amountMinor !== null &&
      !offered.some((preset) => preset.amountMinor === arrived.amountMinor)
        ? [
            ...offered,
            { amountMinor: arrived.amountMinor, currency: offered[0]?.currency ?? "USD" },
          ]
        : offered;
    const choice = offer ? designationChoice(offer) : ({ kind: "none" } as const);
    const hints = checkoutHints({
      amountMinor,
      designationId: offer ? sendableDesignationId(offer, designationId) : null,
    });

    return (
      <>
        {/*
          A `button` and not a link, unlike the event Give this replaced.

          That one had a real destination to fall back to — the event's own page — so it could be
          an anchor that worked with JavaScript stalled. This one does not: `/e/<generalFundId>`
          sends a donor straight back to the organization page, because the event page redirects a
          general fund to the organization it belongs to (`EventPage.tsx`), and an href that
          returns to the page it is on is a loop rather than a fallback.

          The label names the destination because the receipt will. "Give" alone, two rows under
          the name of tonight's fundraiser, is the exact mislabelling this rewrite exists to remove.
        */}
        {presets.length > 0 && (
          <fieldset className="block orggive__amounts">
            <legend className="give__section">Amount</legend>
            <div className="amounts">
              {presets.map((preset) => (
                <button
                  key={preset.amountMinor}
                  type="button"
                  className={
                    amountMinor === preset.amountMinor ? "amount amount--selected" : "amount"
                  }
                  aria-pressed={amountMinor === preset.amountMinor}
                  onClick={() =>
                    setAmountMinor((current) =>
                      current === preset.amountMinor ? null : preset.amountMinor,
                    )
                  }
                >
                  {formatMoneyCompact(preset)}
                </button>
              ))}
            </div>
          </fieldset>
        )}
        {/* Amount, then what it is for, as in every give box (GH-817; `DESIGN.md`). */}
        <DesignationPicker choice={choice} selectedId={designationId} onChoose={setDesignationId} />
        <button
          type="button"
          className="pay landing__give"
          disabled={starting}
          onClick={() => {
            setStarting(true);
            setFailure(null);
            fetchDonationLink(generalFundEventId)
              .then((link) =>
                window.location.assign(`/g/${encodeURIComponent(link.token)}${hints}`),
              )
              .catch(() => {
                // Spoken, because there is nowhere to fall through to. A button that stayed on
                // "Opening…" forever would look like a slow network rather than a refusal.
                setStarting(false);
                setFailure("This could not be opened. Try again in a moment.");
              });
          }}
        >
          {starting ? "Opening…" : "Give to the general fund"}
        </button>
        <Explain className="landing__welcome" label="Not tied to an event" title="The general fund">
          <p>
            A general-fund gift supports the organization&rsquo;s ongoing work rather than one
            campaign or one evening.
          </p>
        </Explain>
        {/* "No account needed" stood here until GH-802 (owner, TestFlight build 24: remove the
            no-sign-in label from every give surface). Giving still asks for nothing. */}
        {failure && (
          <p className="notice notice--error" role="alert">
            {failure}
          </p>
        )}
      </>
    );
  }

  if (heldGifts !== null && !heldClosed) {
    return (
      <>
        <button
          type="button"
          className="pay landing__give"
          disabled={starting}
          onClick={() => {
            setStarting(true);
            setFailure(null);
            fetchHeldGiftLink(organization.id)
              .then((link) => window.location.assign(`/g/${encodeURIComponent(link.token)}`))
              .catch((caught: unknown) => {
                setStarting(false);
                if (caught instanceof ApiError && caught.code === "capability_disabled") {
                  setHeldClosed(true);
                  return;
                }
                setFailure("This could not be opened. Try again in a moment.");
              });
          }}
        >
          {starting ? "Opening…" : "Give"}
        </button>
        {/*
          The server's two sentences, verbatim and in its order: who the gift is made to and what
          it is targeted for, then the terms. Composed server-side so the hold length and the
          recipient can never disagree with the job that releases the money.
        */}
        <Explain
          className="landing__welcome"
          label="Held until it joins"
          title="Where your gift goes"
        >
          <p>{heldGifts.notice}</p>
          <p>{heldGifts.terms}</p>
        </Explain>
        {failure && (
          <p className="notice notice--error" role="alert">
            {failure}
          </p>
        )}
      </>
    );
  }

  /*
    Unclaimed. The server's own wording sits in the header above; this says why there is no
    button, and offers nothing in its place.
  */
  return (
    <Explain className="notice" label="Not set up yet" title="Why you can't give here">
      <p>{"This organization hasn't set up giving on GiveHere yet."}</p>
    </Explain>
  );
}
